import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import { useAuth } from "../../Common/Configurations/AuthContext";
import Login from "../../Authentication/Login";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import moment from "moment";
import ReportTabs from "../Reports/ReportTabs";
import { FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const PurchasesReport = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [fromDate, setFromDate] = useState(moment().startOf("month").toDate());
  const [toDate, setToDate] = useState(moment().endOf("month").toDate());
  const [status, setStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("all");

  // Get user's currency from auth context
  const getUserCurrency = () => {
    return user?.currency || "INR"; // Fallback to INR if currency is not available
  };

  // Get branches for the dropdown
  const GetBranches = async () => {
    try {
      const res = await apiService.getAll(
        `${config.GETBRANCHBYCOMPANY}/${user?.cid}`
      );
      setBranches(res.data || res);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const calculateTotal = () => {
    return {
      totalQuantity: reports.reduce(
        (sum, report) => sum + report.totalQuantity,
        0
      ),
      subTotal: reports.reduce((sum, report) => sum + report.subTotal, 0),
      discount: reports.reduce((sum, report) => sum + report.discount, 0),
      tax: reports.reduce((sum, report) => sum + report.tax, 0),
      grandTotal: reports.reduce((sum, report) => sum + report.grandTotal, 0),
    };
  };

  const GetReports = async () => {
    try {
      const params = new URLSearchParams();
      params.append("userId", user.id);
      if (selectedBranch && selectedBranch !== "all") {
        params.append("branchId", selectedBranch);
      }
      if (fromDate)
        params.append("fromDate", moment(fromDate).format("YYYY-MM-DD"));
      if (toDate) params.append("toDate", moment(toDate).format("YYYY-MM-DD"));
      if (status && status !== "all") params.append("status", status);
      if (selectedItem) {
        params.append("itemNames", selectedItem.itemNames);
      }

      const res = await apiService.getAll(
        `${config.PURCHASESREPORT}/${user.id}?${params.toString()}`
      );
      setReports(res);
    } catch (error) {
      console.error("Error fetching reports:", error);
    }
  };

  const searchItems = async (keyword) => {
    if (!keyword) {
      setItemSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const res = await apiService.getAll(
        `${config.SEARCHPURCHASESITEMS}?Keyword=${keyword}`
      );
      setItemSuggestions(res);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error searching items:", error);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setSearchQuery(item.itemNames);
    setItemSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    GetReports();
  };

  const handleClear = () => {
    setFromDate(moment().startOf("month").toDate());
    setToDate(moment().endOf("month").toDate());
    setStatus("all");
    setSearchQuery("");
    setSelectedItem(null);
    setItemSuggestions([]);
    setSelectedBranch("all");
  };

  const exportToExcel = () => {
    const currency = getUserCurrency();

    // Prepare the data with formatted dates and numbers
    const excelData = reports.map((report) => ({
      "Invoice No": report.invoiceNumber,
      "Item Name": report.itemNames,
      Quantity: report.totalQuantity,
      "Sub Total": report.subTotal.toFixed(2),
      Discount: report.discount.toFixed(2),
      Tax: report.tax.toFixed(2),
      "Grand Total": report.grandTotal.toFixed(2),
      Status: report.status,
      "Invoice Date": moment(report.invoiceDate).format("DD-MMM-YYYY"),
      "Due Date": moment(report.dueDate).format("DD-MMM-YYYY"),
    }));

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Add header row with styles
    const header = [
      "Invoice No",
      "Item Name",
      "Quantity",
      "Sub Total",
      "Discount",
      "Tax",
      "Grand Total",
      "Status",
      "Invoice Date",
      "Due Date",
    ];
    XLSX.utils.sheet_add_aoa(worksheet, [header], { origin: "A1" });

    // Add total row
    const totals = calculateTotal();
    XLSX.utils.sheet_add_aoa(
      worksheet,
      [
        [
          "",
          "Total:",
          totals.totalQuantity,
          totals.subTotal.toFixed(2),
          totals.discount.toFixed(2),
          totals.tax.toFixed(2),
          totals.grandTotal.toFixed(2),
          "",
          "",
          "",
        ],
      ],
      { origin: -1 }
    );

    // Set column widths
    const wscols = [
      { wch: 15 }, // Invoice No
      { wch: 25 }, // Item Name
      { wch: 10 }, // Quantity
      { wch: 12 }, // Sub Total
      { wch: 12 }, // Discount
      { wch: 12 }, // Tax
      { wch: 12 }, // Grand Total
      { wch: 15 }, // Status
      { wch: 15 }, // Invoice Date
      { wch: 15 }, // Due Date
    ];
    worksheet["!cols"] = wscols;

    // Create workbook and export
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Purchases Reports");
    XLSX.writeFile(
      workbook,
      `Purchases_Report_${moment().format("YYYYMMDD")}.xlsx`
    );
  };

  const exportToPDF = () => {
    const currency = getUserCurrency();
    const doc = new jsPDF();

    // Title
    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80); // Dark blue color
    doc.text("PURCHASES REPORT", 105, 15, { align: "center" });

    // Filters information
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0); // Black color
    let filtersText = "Filters: ";
    if (selectedBranch && selectedBranch !== "all") {
      const branchName =
        branches.find((b) => b.branchId === selectedBranch)?.branchName ||
        selectedBranch;
      filtersText += `Branch: ${branchName} `;
    }
    if (fromDate)
      filtersText += `From: ${moment(fromDate).format("DD-MMM-YYYY")} `;
    if (toDate) filtersText += `To: ${moment(toDate).format("DD-MMM-YYYY")} `;
    if (status !== "all") filtersText += `Status: ${status} `;
    if (searchQuery) filtersText += `Search: ${searchQuery}`;

    doc.text(filtersText, 14, 25);
    doc.text(`Generated on: ${moment().format("DD-MMM-YYYY hh:mm A")}`, 14, 30);

    // Table data
    const tableData = reports.map((report) => [
      report.invoiceNumber,
      report.itemNames,
      report.totalQuantity,
      `${currency} ${report.subTotal.toFixed(2)}`,
      `${currency} ${report.discount.toFixed(2)}`,
      `${currency} ${report.tax.toFixed(2)}`,
      `${currency} ${report.grandTotal.toFixed(2)}`,
      report.status,
      moment(report.invoiceDate).format("DD-MMM-YYYY"),
      moment(report.dueDate).format("DD-MMM-YYYY"),
    ]);

    // Add total row
    const totals = calculateTotal();
    tableData.push([
      "",
      "Total:",
      totals.totalQuantity,
      `${currency} ${totals.subTotal.toFixed(2)}`,
      `${currency} ${totals.discount.toFixed(2)}`,
      `${currency} ${totals.tax.toFixed(2)}`,
      `${currency} ${totals.grandTotal.toFixed(2)}`,
      "",
      "",
      "",
    ]);

    // Table styles
    doc.autoTable({
      head: [
        [
          "Invoice No",
          "Item Name",
          "Quantity",
          "Sub Total",
          "Discount",
          "Tax",
          "Grand Total",
          "Status",
          "Invoice Date",
          "Due Date",
        ],
      ],
      body: tableData,
      startY: 35,
      margin: { top: 20 },
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: "linebreak",
        textColor: [0, 0, 0], // Black text
        fillColor: [255, 255, 255], // White background
        lineColor: [221, 221, 221], // Light gray borders
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [41, 128, 185], // Blue header
        textColor: 255, // White text
        fontStyle: "bold",
        halign: "center",
      },
      bodyStyles: {
        halign: "left",
      },
      columnStyles: {
        0: { cellWidth: 15 }, // Invoice No
        1: { cellWidth: 25 }, // Item Name
        2: { cellWidth: 10, halign: "right" }, // Quantity
        3: { cellWidth: 12, halign: "right" }, // Sub Total
        4: { cellWidth: 12, halign: "right" }, // Discount
        5: { cellWidth: 12, halign: "right" }, // Tax
        6: { cellWidth: 12, halign: "right" }, // Grand Total
        7: { cellWidth: 15 }, // Status
        8: { cellWidth: 15 }, // Invoice Date
        9: { cellWidth: 15 }, // Due Date
      },
      // Style for the last row (total)
      didParseCell: function (data) {
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fontStyle = "bold";
        }
      },
      didDrawPage: function (data) {
        // Footer with page numbers
        doc.setFontSize(10);
        const pageCount = doc.internal.getNumberOfPages();
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: "center" }
        );
      },
    });

    doc.save(`Purchases_Report_${moment().format("YYYYMMDD")}.pdf`);
  };

  const handlePrint = () => {
    const currency = getUserCurrency();
    const totals = calculateTotal();
    const printWindow = window.open("", "_blank");
    const printContent = `
    <html>
      <head>
        <title>Purchases Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #2c3e50; text-align: center; }
          .filters { margin-bottom: 20px; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
          th { background-color: #2980b9; color: white; padding: 8px; text-align: left; }
          td { padding: 6px; border-bottom: 1px solid #ddd; }
          .text-right { text-align: right; }
          .footer { margin-top: 20px; font-size: 12px; text-align: center; }
          .total-row { font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>PURCHASES REPORT</h1>
        <div class="filters">
          <strong>Filters:</strong>
          ${
            selectedBranch && selectedBranch !== "all"
              ? `Branch: ${
                  branches.find((b) => b.branchId === selectedBranch)
                    ?.branchName || selectedBranch
                } `
              : ""
          }
          ${fromDate ? `From: ${moment(fromDate).format("DD-MMM-YYYY")} ` : ""}
          ${toDate ? `To: ${moment(toDate).format("DD-MMM-YYYY")} ` : ""}
          ${status !== "all" ? `Status: ${status} ` : ""}
          ${searchQuery ? `Search: ${searchQuery}` : ""}
        </div>
        <div class="filters">Generated on: ${moment().format(
          "DD-MMM-YYYY hh:mm A"
        )}</div>
        <table>
          <thead>
            <tr>
              <th>Invoice No</th>
              <th>Item</th>
              <th>Qty</th>
              <th>Sub Total</th>
              <th>Discount</th>
              <th>Tax</th>
              <th>Grand Total</th>
              <th>Status</th>
              <th>Invoice Date</th>
              <th>Due Date</th>
            </tr>
          </thead>
          <tbody>
            ${reports
              .map(
                (report) => `
              <tr>
                <td>${report.invoiceNumber}</td>
                <td>${report.itemNames}</td>
                <td class="text-right">${report.totalQuantity}</td>
                <td class="text-right">${currency} ${report.subTotal.toFixed(
                  2
                )}</td>
                <td class="text-right">${currency} ${report.discount.toFixed(
                  2
                )}</td>
                <td class="text-right">${currency} ${report.tax.toFixed(2)}</td>
                <td class="text-right">${currency} ${report.grandTotal.toFixed(
                  2
                )}</td>
                <td>${report.status}</td>
                <td>${moment(report.invoiceDate).format("DD-MMM-YYYY")}</td>
                <td>${moment(report.dueDate).format("DD-MMM-YYYY")}</td>
              </tr>
            `
              )
              .join("")}
            <tr class="total-row">
              <td colspan="2" class="text-end">Total:</td>
              <td class="text-right">${totals.totalQuantity}</td>
              <td class="text-right">${currency} ${totals.subTotal.toFixed(
      2
    )}</td>
              <td class="text-right">${currency} ${totals.discount.toFixed(
      2
    )}</td>
              <td class="text-right">${currency} ${totals.tax.toFixed(2)}</td>
              <td class="text-right">${currency} ${totals.grandTotal.toFixed(
      2
    )}</td>
              <td></td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>
        <div class="footer">
          Page 1 of 1
        </div>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              window.close();
            }, 200);
          };
        </script>
      </body>
    </html>
  `;

    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
  };
  const getStatusBadge = (status, dueDate) => {
    if (status === "PAID") {
      return <span className="badge bg-success">PAID</span>;
    }
    if (status === "UNPAID") {
      return <span className="badge bg-danger">UNPAID</span>;
    }
    return <span className="badge bg-primary">DRAFT</span>;
  };
  // useEffect(() => {
  //   if (user && !loading) {
  //     GetBranches();
  //     GetReports();
  //   }
  // }, [user, loading]);

  useEffect(() => {
    if (user && !loading) {
      GetBranches();
      GetReports();
    }
  }, [user, loading, fromDate, toDate, status, selectedItem, selectedBranch]);

  if (loading) return <p>Loading...</p>;
  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2">
                  <ReportTabs pageName="PURCHASESREPORT" />
                </div>
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-md-12 d-flex justify-content-end">
                      <Dropdown as={ButtonGroup}>
                        <Dropdown.Toggle
                          variant="primary"
                          id="dropdown-basic"
                          className="btn btn-primary"
                        >
                          <i className="ri-download-line me-1"></i>
                          Export
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={exportToExcel}>
                            <i className="ri-file-excel-line me-2 text-success"></i>
                            Excel
                          </Dropdown.Item>
                          <Dropdown.Item onClick={exportToPDF}>
                            <i className="ri-file-pdf-line me-2 text-danger"></i>
                            PDF
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                      <button
                        className="btn btn-secondary ms-2"
                        onClick={handlePrint}
                      >
                        <i className="ri-printer-line me-1"></i>
                        Print
                      </button>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-3">
                      <label className="form-label">Branch</label>
                      <select
                        className="form-select"
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                      >
                        <option value="all">All Branches</option>
                        {branches.map((branch) => (
                          <option key={branch.branchId} value={branch.branchId}>
                            {branch.branchName}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">Search Item</label>
                      <div className="position-relative">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Type item name..."
                          value={searchQuery}
                          onChange={(e) => {
                            const value = e.target.value;
                            setSearchQuery(value);
                            searchItems(value);
                          }}
                        />
                        {showSuggestions && itemSuggestions.length > 0 && (
                          <div className="position-absolute w-100 bg-light border rounded mt-1 z-3">
                            <ul className="list-unstyled mb-0">
                              {itemSuggestions.map((item) => (
                                <li
                                  key={item.itemId}
                                  className="px-3 py-2 cursor-pointer hover-bg-light"
                                  onClick={() => handleItemSelect(item)}
                                >
                                  {item.itemNames}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">From Date</label>
                      <div className="position-relative">
                        <DatePicker
                          selected={fromDate}
                          onChange={(date) => setFromDate(date)}
                          dateFormat="dd-MMM-yyyy"
                          className="form-control pe-4"
                          placeholderText="Select from date"
                          showIcon
                          icon={
                            <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">To Date</label>
                      <div className="position-relative">
                        <DatePicker
                          selected={toDate}
                          onChange={(date) => setToDate(date)}
                          minDate={fromDate}
                          dateFormat="dd-MMM-yyyy"
                          className="form-control pe-4"
                          placeholderText="Select to date"
                          showIcon
                          icon={
                            <FaCalendarAlt className="position-absolute end-0 top-50 translate-middle-y me-2" />
                          }
                        />
                      </div>
                    </div>
                    <div className="col-md-2">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                      >
                        <option value="All">All</option>
                        <option value="PAID">PAID</option>
                        <option value="UNPAID">UNPAID</option>
                      </select>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-12 d-flex justify-content-end">
                      <button
                        className="btn btn-primary me-2"
                        onClick={handleSearch}
                      >
                        Search
                      </button>
                      <button
                        className="btn btn-danger me-2"
                        onClick={handleClear}
                      >
                        Clear
                      </button>
                    </div>
                  </div>

                  <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-0">
                      <thead className="bg-soft-success bg-gradient">
                        <tr>
                          <th>Invoice No</th>
                          <th>Item Name</th>
                          <th className="text-end">Quantity</th>
                          <th className="text-end">Sub Total</th>
                          <th className="text-end">Discount</th>
                          <th className="text-end">Tax</th>
                          <th className="text-end">Grand Total</th>
                          <th>Status</th>
                          <th>Invoice Date</th>
                          <th>Due Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.length > 0 ? (
                          <>
                            {reports.map((report, index) => (
                              <tr key={index}>
                                <td>{report.invoiceNumber}</td>
                                <td>{report.itemNames}</td>
                                <td className="text-end">
                                  {report.totalQuantity}
                                </td>
                                <td className="text-end">
                                  {getUserCurrency()}{" "}
                                  {report.subTotal.toFixed(2)}
                                </td>
                                <td className="text-end">
                                  {getUserCurrency()}{" "}
                                  {report.discount.toFixed(2)}
                                </td>
                                <td className="text-end">
                                  {getUserCurrency()} {report.tax.toFixed(2)}
                                </td>
                                <td className="text-end">
                                  {getUserCurrency()}{" "}
                                  {report.grandTotal.toFixed(2)}
                                </td>
                                <td>{getStatusBadge(report.status)}</td>
                                <td>
                                  {moment(report.invoiceDate).format(
                                    "DD-MMM-YYYY"
                                  )}
                                </td>
                                <td>
                                  {moment(report.dueDate).format("DD-MMM-YYYY")}
                                </td>
                              </tr>
                            ))}
                            <tr className="fw-bold">
                              <td colSpan="2" className="text-end">
                                Total:
                              </td>
                              <td className="text-end">
                                {calculateTotal().totalQuantity}
                              </td>
                              <td className="text-end">
                                {getUserCurrency()}{" "}
                                {calculateTotal().subTotal.toFixed(2)}
                              </td>
                              <td className="text-end">
                                {getUserCurrency()}{" "}
                                {calculateTotal().discount.toFixed(2)}
                              </td>
                              <td className="text-end">
                                {getUserCurrency()}{" "}
                                {calculateTotal().tax.toFixed(2)}
                              </td>
                              <td className="text-end">
                                {getUserCurrency()}{" "}
                                {calculateTotal().grandTotal.toFixed(2)}
                              </td>
                              <td></td>
                              <td></td>
                              <td></td>
                            </tr>
                          </>
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-center py-4">
                              No records found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchasesReport;
